import { useEffect, useMemo, useRef, useState } from "react";
import { LuTrash2 } from "react-icons/lu";
import { codeSummary, loadServices, saveServices, serviceLabel } from "./serviceCatalog.js";
import InternalTable from "./InternalTable.jsx";
import { camelKey, emptyForm, formFromService, listText, serviceFromForm, ServiceFields } from "./ServiceForm.jsx";
import "./InternalWork.scss";

const countText = (items, noun) => {
    const count = (items || []).length;
    if (!count) return "";
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
};

const ServiceGuide = () => (
    <div className="internal-work__service-guide">
        <section>
            <h3>Catalog</h3>
            <p>A plan picks a service from this list and keeps its CPT, HCPCS, and revenue codes.</p>
        </section>
        <section>
            <h3>Standalone cost share</h3>
            <p>The service row is what the member pays when nothing more specific applies: covered, copay, percent, and deductible, in network and out of network.</p>
        </section>
        <section>
            <h3>Place of service</h3>
            <p>Add a line when one place pays differently. Coverage modifiers on that line are 95 and GT for telehealth, 26 for the professional component, TC for the technical component, and 33 for a preventive service. Pricing modifiers such as 25, 51, 59, and QK stay with the repricer.</p>
        </section>
        <section>
            <h3>Same-day visit</h3>
            <p>Basic labs can follow a PCP visit, an OB/GYN visit, or a specialist visit. Basic diagnostic radiology and basic lab imaging can follow a PCP visit or a specialist visit. The lab line has no rendering practitioner and is matched to that visit. Member’s PCP means the visit was rendered by the member’s PCP.</p>
        </section>
        <section>
            <h3>Emergency room</h3>
            <p>The service row is the emergent facility cost share. Separate lines set the non-emergent facility, the emergent professional, and the non-emergent professional cost shares, plus the non-emergent penalty and the max days.</p>
        </section>
        <section>
            <h3>Penalties</h3>
            <p>A specialist visit has a no-referral penalty. Inpatient hospital has a penalty and a note for when that penalty does not apply.</p>
            <p>PCP specialty not elected is the penalty when a member sees a doctor in a PCP specialty who is not the PCP they elected. Family medicine, internal medicine, and pediatrics count as PCP specialties. A visit with the elected PCP uses the PCP cost share. A visit with a different doctor in one of those specialties takes this penalty. Enter the amount on the plan.</p>
        </section>
        <section>
            <h3>Precertification</h3>
            <p>Inpatient hospital, outpatient surgery, and advanced diagnostic can require precertification. Set whether it is required, the penalty when authorization is missing, and whether the provider or the member pays it.</p>
        </section>
        <section>
            <h3>Shared cap</h3>
            <p>On Limits, name the other services that draw down the same cap. For example, 30 days / year shared with Chiropractice, Acupuncture.</p>
        </section>
        <section>
            <h3>Rules</h3>
            <p>Each service has rules that say how a claim pays, written as if, else if, and else. It also has a plain-language summary, a daily limit, and whether it counts toward the out-of-pocket maximum. Every plan uses these rules. Choose Customize for this plan when one plan pays differently, and Use the catalog to go back.</p>
        </section>
        <section>
            <h3>Provider types</h3>
            <p>Each service lists the types of providers who can bill it. These are called taxonomy codes. A PCP and a specialist use the same visit codes, so the provider type tells the two apart. The plan picks what happens when a different type of provider sends the bill. A service with no provider types accepts any provider.</p>
        </section>
        <section>
            <h3>Where it is billed</h3>
            <p>A doctor's bill lists the place of service, such as an office or a hospital. A hospital or clinic bill lists the type of bill and revenue codes instead. One visit can send both bills: one for the doctor and one for the building.</p>
        </section>
    </div>
);

const ServicesPage = () => {
    const [services, setServices] = useState(() => loadServices());
    const [query, setQuery] = useState("");
    const [draft, setDraft] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [error, setError] = useState("");
    const [sidebarWidth, setSidebarWidth] = useState(420);
    const [dragging, setDragging] = useState(false);
    const [panel, setPanel] = useState(null);
    const [guide, setGuide] = useState(false);
    const splitRef = useRef(null);
    const asideRef = useRef(null);
    const nameRef = useRef(null);
    const open = Boolean(draft) || guide;

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return services;
        return services.filter((service) => {
            const hay = [
                serviceLabel(service.key),
                service.key,
                codeSummary(service.cptCodes, service.cptCodeGroups),
                codeSummary(service.hcpcsCodes, service.hcpcsCodeGroups),
                codeSummary(service.revenueCode, service.revenueCodeGroups),
                listText(service.taxonomyCodes),
                listText(service.typeOfBill),
            ].join(" ").toLowerCase();
            return hay.includes(needle);
        });
    }, [services, query]);

    const write = (next) => {
        saveServices(next);
        setServices(next);
    };

    const openNew = () => {
        setGuide(false);
        setEditingKey(null);
        setDraft(emptyForm());
        setError("");
    };

    const openEdit = (service) => {
        setGuide(false);
        setEditingKey(service.key);
        setDraft(formFromService(service));
        setError("");
    };

    const showGuide = () => {
        if (guide) {
            setGuide(false);
            return;
        }
        setDraft(null);
        setEditingKey(null);
        setError("");
        setGuide(true);
    };

    const cancel = () => {
        setDraft(null);
        setEditingKey(null);
        setError("");
    };

    const save = (event) => {
        event.preventDefault();
        if (!draft) return;
        const name = draft.name.trim();
        if (!name) {
            setError("Give the service a name.");
            return;
        }
        const key = editingKey && name === serviceLabel(editingKey) ? editingKey : camelKey(name);
        if (!key) {
            setError("Give the service a name.");
            return;
        }
        if (services.some((service) => service.key === key && service.key !== editingKey)) {
            setError("A service with that name is already in the list.");
            return;
        }
        const nextService = serviceFromForm(draft, key);
        const next = editingKey
            ? services.map((service) => (service.key === editingKey ? nextService : service))
            : [...services, nextService];
        write(next);
        cancel();
    };

    const remove = (key) => {
        write(services.filter((service) => service.key !== key));
        if (editingKey === key) cancel();
    };

    const setField = (field) => (value) => setDraft((current) => (current ? { ...current, [field]: value } : current));

    useEffect(() => {
        if (draft || guide) {
            setPanel(draft ? { draft, editingKey } : { guide: true });
            return undefined;
        }
        const node = asideRef.current;
        if (!node) return undefined;
        let settled = false;
        const finish = (event) => {
            if (settled || (event && event.target !== node) || (event && event.propertyName !== "width")) return;
            settled = true;
            setPanel(null);
        };
        node.addEventListener("transitionend", finish);
        const timer = window.setTimeout(() => finish(), 320);
        return () => {
            node.removeEventListener("transitionend", finish);
            window.clearTimeout(timer);
        };
    }, [draft, editingKey, guide]);

    const drafting = Boolean(draft);
    useEffect(() => {
        if (drafting) nameRef.current?.focus();
    }, [drafting, editingKey]);

    const resizeSidebar = (event) => {
        event.preventDefault();
        const handle = event.currentTarget;
        try {
            handle.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone.
        }
        setDragging(true);
        const move = (moveEvent) => {
            const bounds = splitRef.current?.getBoundingClientRect();
            if (!bounds) return;
            const next = bounds.right - moveEvent.clientX;
            const min = 280;
            const max = Math.max(min, bounds.width - 280);
            setSidebarWidth(Math.round(Math.min(max, Math.max(min, next))));
        };
        const stop = () => {
            setDragging(false);
            handle.removeEventListener("pointermove", move);
            handle.removeEventListener("pointerup", stop);
        };
        handle.addEventListener("pointermove", move);
        handle.addEventListener("pointerup", stop);
    };

    const visible = draft || panel?.draft || null;
    const visibleKey = draft ? editingKey : panel?.editingKey;
    const sidebarTitle = !visible ? "Service" : visibleKey ? (visible.name.trim() || "Edit service") : "Add service";

    return (
        <div className="container internal-work">
            <div className="internal-work__frame">
                <div
                    ref={splitRef}
                    className="internal-work__split"
                >
                    <div className="internal-work__main">
                        <header className="internal-work__top">
                            <div>
                                <p className="internal-work__eyebrow">Internal</p>
                                <h1>Services</h1>
                                <p className="internal-work__lead">CPT, HCPCS, and revenue codes for each service. This list stays in the browser for now.</p>
                            </div>
                            <div className="internal-work__heading-actions">
                                <button type="button" className={`internal-work__guide-link${guide ? " is-on" : ""}`} aria-expanded={guide} onClick={showGuide}>Guide</button>
                                <button type="button" className="btn edit-btn" onClick={openNew}>Add service</button>
                            </div>
                        </header>
                        <input
                            className="internal-work__search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Find a service or code"
                            aria-label="Find a service or code"
                        />
                        <div className="internal-work__sheet">
                            <InternalTable
                                columns={["Service", "CPT", "HCPCS", "Revenue", "Place of service", "Type of bill", "Taxonomy", "Rules", ""]}
                                rows={shown.map((service) => ({
                                    key: service.key,
                                    className: service.key === editingKey ? "is-selected" : undefined,
                                    onClick: () => openEdit(service),
                                    cells: [
                                        serviceLabel(service.key),
                                        codeSummary(service.cptCodes, service.cptCodeGroups),
                                        codeSummary(service.hcpcsCodes, service.hcpcsCodeGroups),
                                        codeSummary(service.revenueCode, service.revenueCodeGroups),
                                        listText(service.placesOfService),
                                        listText(service.typeOfBill),
                                        countText(service.taxonomyCodes, "code"),
                                        countText(service.rules, "line"),
                                        <button
                                            key="remove"
                                            type="button"
                                            className="internal-work__icon is-danger"
                                            aria-label={`Remove ${serviceLabel(service.key)}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                remove(service.key);
                                            }}
                                        >
                                            <LuTrash2 />
                                        </button>,
                                    ],
                                }))}
                            />
                        </div>
                        {shown.length === 0 && <p className="internal-work__end">No service matches that search.</p>}
                    </div>
                    <aside
                        ref={asideRef}
                        className={`internal-work__json is-drawer${open ? " is-open" : ""}${dragging ? " is-dragging" : ""}`}
                        style={{ "--sidebar-width": `${sidebarWidth}px` }}
                        aria-label={guide || panel?.guide ? "Service guide" : "Service form"}
                        aria-hidden={open ? undefined : true}
                    >
                        <div
                            className="internal-work__json-drag"
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize sidebar"
                            onPointerDown={resizeSidebar}
                        />
                        {(guide || panel?.guide) && !draft ? (
                            <div className="internal-work__drawer-body">
                                <h2>How a plan uses a service</h2>
                                <ServiceGuide />
                            </div>
                        ) : visible && (
                            <div className="internal-work__drawer-body">
                                <h2>{sidebarTitle}</h2>
                                <form className="internal-work__form" onSubmit={save}>
                                    <ServiceFields
                                        form={visible}
                                        setField={setField}
                                        nameRef={nameRef}
                                        rulesKey={`${visibleKey || "new"}-rules`}
                                    />
                                    {error && open && <p className="internal-work__service-error">{error}</p>}
                                    <div className="internal-work__form-actions">
                                        <button type="button" className="internal-work__reset" onClick={cancel}>Cancel</button>
                                        <button type="submit" className="internal-work__submit">{visibleKey ? "Save service" : "Add service"}</button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default ServicesPage;
